import React, { useEffect, useMemo, useState } from "react";
import { pdfjs } from "react-pdf";
import { Layout } from "../../components/Layout/Layout";
import { useAuth } from "../../context/AuthContext";
import { apiCall, apiUpload } from "../../utils/apiProxy";
import client from "../../api/client";
import "./index.css";
import UpdateExcel from "../../context/updateExcel";
import User from "../../components/User";
import loadingcat from "../../assets/loadingcat.gif";
import { useInHouse } from "../../context/inHousePermissions";
import Metrics from "../../components/Metrics";
import { isEditorPriority } from "../../utils/expressHelpers";
import ApprovalPopup from "../../components/ApprovalPopup";
import SendToClientPopup, { sentToast } from "../../components/SendToClientPopup";


pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


interface Order {
  userId: string;
  username: string;
  number: string;
  password: string;
  status: string;
  purchaseDate: Date;
  city: string;
  recipient: string;
  payment: string;
  fullPayment: string;
  platform: string;
  comment: string;
  commentLogis: string;
  express: 'none' | 'express_print' | 'express_edit' | 'urep' | null;
  salesMan: string;
  userNumber: string;
  address: string;
  editor: string;
  logist: string;
  link?: string;
  pages?: string;
  author?: string;
  bookName?: string;
  courierNumber: string;
  paymentId?: { paymentId: string; amount: number; createdAt: Date }[];

  isInfluencer: boolean;

  deadline: string;
  daysUntilDeadline: number;

  whatsapp: string;
  finishedAt?: Date | null;
}

interface Notifications {
  message: string;
  timestamp: Date;
  userId: string;
}

const getPageCount = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async () => {
      try {
        const typedArray = new Uint8Array(reader.result as ArrayBuffer);
        const pdfDoc = await pdfjs.getDocument({ data: typedArray }).promise;
        resolve(pdfDoc.numPages);
      } catch (error) {
        reject(error);
        console.error("oops", error);
      }
    };

    reader.onerror = (err) => {
      reject(err);
      console.error("oops", err);
    };

    reader.readAsArrayBuffer(file);
  });
};

const getDaysUntilDeadline = (finishedAt: Date | null | undefined): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!finishedAt || isNaN(new Date(finishedAt).getTime())) {
    return 1; // нет finishedAt или невалидная дата = дедлайн завтра
  }

  const deadlineDate = new Date(finishedAt);
  deadlineDate.setHours(0, 0, 0, 0);
  deadlineDate.setDate(deadlineDate.getDate() + 2);

  return Math.round(
    (deadlineDate.getTime() - today.getTime()) / (1000 * 3600 * 24)
  );
};

const getDeadlineLabel = (days: number): string => {
  if (isNaN(days)) return "Завтра";
  if (days === 0) return "Сегодня";
  if (days === 1) return "Завтра";
  if (days === 2) return "Послезавтра";
  if (days === -1) return "Вчера";
  if (days === -2) return "Позавчера";
  if (days < -2) return `${Math.abs(days)} дней назад`;
  return `Через ${days} дней`;
};

const fetchOrders = async (): Promise<Order[]> => {
  const { data } = await client.get("/orders", {
    params: {
      status: ["Заполнил", "Отредачено", "Готово под печать"],
    },
  });
  const users = data.map((item: any) => {
    const parsed = item.finishedAt ? new Date(item.finishedAt) : null;
    if (parsed && isNaN(parsed.getTime())) {
      console.log("finishedAt INVALID:", item.username, item.finishedAt, typeof item.finishedAt);
    }
    return {
      ...item,
      purchaseDate: new Date(item.purchaseDate),
      finishedAt: item.finishedAt ? new Date(item.finishedAt) : null,
      isInfluencer: item.isInfluencer || false,
    };
  }) as Order[];

  return users;
};

const EditorNew: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);
  const [approvalOrder, setApprovalOrder] = useState<Order | null>(null);
  // «Скинуть клиенту» и у кого клиент прислал правки к отправленной вёрстке.
  const [sendOrder, setSendOrder] = useState<Order | null>(null);
  const [sentNotice, setSentNotice] = useState<string | null>(null);
  const [withChanges, setWithChanges] = useState<Set<string>>(new Set());
  const fetchChanges = async () => {
    try {
      const { data } = await client.get<number[]>("/proofs/admin/changes");
      setWithChanges(new Set((data || []).map(String)));
    } catch (error) {
      console.error("Error fetching client changes", error);
    }
  };

  // Колонка «snippex»: контакт клиента в Snippex — ник Telegram, нет ника — телефон, нет и его — «Подключен».
  const [snippexLinked, setSnippexLinked] = useState<Map<string, string>>(new Map());
  const fetchSnippexLinked = async (userIds: Array<number | string>) => {
    try {
      const { data } = await client.post<{
        linked: { userId: number; username: string | null; phone: string | null }[] | null;
      }>("/proofs/admin/snippex-linked", { userIds: userIds.map(Number) });
      setSnippexLinked(
        new Map((data?.linked || []).map((c) => [String(c.userId), c.username || c.phone || "Подключен"]))
      );
    } catch (error) {
      console.error("Error fetching Snippex links", error);
    }
  };

  const [isExpress, setIsExpress] = useState<boolean>(false);
  const [pages, setPages] = useState<string>("");
  const [bookName, setBookName] = useState<string>("");
  const [author, setAuthor] = useState<string>(""); // NEW
  const [file1, setFile1] = useState<File | null>(null); // NEW
  const [file2, setFile2] = useState<File | null>(null);
  const { currentUser } = useAuth();

  const [selectedUser, setSelectedUser] = useState<Order | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [selectedEditor, setSelectedEditor] = useState<string | null>(null);

  const { inHouseRecords } = useInHouse();

  const allUsers = Object.values(inHouseRecords);
  const currentUserRecord = currentUser ? inHouseRecords[String(currentUser.id)] : undefined;

  const [assignEditorPopupOpen, setAssignEditorPopupOpen] = useState(false);
  const [assignEditorName, setAssignEditorName] = useState("");
  const [selectedOrdersToAssign, setSelectedOrdersToAssign] = useState<
    string[]
  >([]);

  const handleCheckboxChange = (username: string, checked: boolean) => {
    if (checked) {
      setSelectedOrdersToAssign((prev) => [...prev, username]);
    } else {
      setSelectedOrdersToAssign((prev) => prev.filter((u) => u !== username));
    }
  };

  const handleAssignEditorSave = async () => {
    try {
      for (const username of selectedOrdersToAssign) {
        await client.patch(`/orders/${username}`, { editor: assignEditorName });
      }
      // update local state
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          selectedOrdersToAssign.includes(order.username)
            ? { ...order, editor: assignEditorName }
            : order
        )
      );
      // reset selection and close popup
      setSelectedOrdersToAssign([]);
      setAssignEditorPopupOpen(false);
    } catch (error) {
      console.error("Error assigning editor", error);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      const users = await fetchOrders();
      const mappedUsers = users
        .filter((order) => !["Архивировано"].includes(order.status))
        .map((order) => {
          const days = getDaysUntilDeadline(order.finishedAt);
          return {
            ...order,
            daysUntilDeadline: days,
            deadline: getDeadlineLabel(days),
          };
        });

      mappedUsers.sort(
        (a, b) => a.daysUntilDeadline - b.daysUntilDeadline
      );

      setOrders(mappedUsers);
      fetchSnippexLinked(mappedUsers.map((o) => o.userId));
    };
    initializeData();
    fetchChanges();
  }, []);

  const handleRowClick = async (user: Order) => {
    setSelectedUser(user);
  };

  const handleCloseUser = () => {
    setSelectedUser(null);
  };

  const handleEditOpen = (user: string) => {
    window.open(`/editor/${user}`, "_blank");
  };

  const fetchBookData = async (userId: string) => {
    try {
      const response = await apiCall(`/cover/admin/${userId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch book data: ${response.statusText}`);
      }
      const data = await response.json();
      return {
        bookName: data.bookName || "",
        authorName: data.authorName || "",
      };
    } catch (error) {
      console.error("Error fetching book data:", error);
      return {
        bookName: "",
        authorName: "",
      };
    }
  };

  const handleDoneClick = async (order: Order) => {
    setCurrentOrder(order);
    setShowPopup(true);

    try {
      const { bookName, authorName } = await fetchBookData(order.userId);
      setBookName(bookName);
      setAuthor(authorName);
    } catch (error) {
      console.error("Error loading book details for popup:", error);
      setBookName("");
      setAuthor("");
    }
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setPages("");
    setBookName("");
    setIsExpress(false);
    setFile1(null);
    setFile2(null);
    setCurrentOrder(null);
  };

  const handleSaveLink = async () => {
    if (!currentOrder) return;
    setLoadingSave(true);
    try {
      // 1) Upload file1 and file2
      const pdfUrls: string[] = [];
      if (file1) {
        const formData1 = new FormData();
        formData1.append("file", file1);

        const response1 = await apiUpload("/upload", formData1);
        if (!response1.ok) {
          throw new Error(`File1 upload failed: ${response1.statusText}`);
        }
        const data1 = await response1.text();
        pdfUrls.push(data1);
      }

      if (file2) {
        const formData2 = new FormData();
        formData2.append("file", file2);

        const response2 = await apiUpload("/upload", formData2);
        if (!response2.ok) {
          throw new Error(`File2 upload failed: ${response2.statusText}`);
        }
        const data2 = await response2.text();
        pdfUrls.push(data2);
      }

      // Calculate the day in UTC+5
      const nowLocal = new Date();
      const localOffsetMs = nowLocal.getTimezoneOffset() * 60 * 1000;
      const utcMs = nowLocal.getTime() + localOffsetMs;
      const utcPlus5Ms = utcMs + 5 * 60 * 60 * 1000;
      const nowInUTC5 = new Date(utcPlus5Ms);

      // Decide if we use today or tomorrow based on the hour
      let storeDate: Date;
      if (nowInUTC5.getHours() < 10) {
        storeDate = new Date(nowInUTC5);
      } else {
        storeDate = new Date(
          nowInUTC5.getFullYear(),
          nowInUTC5.getMonth(),
          nowInUTC5.getDate() + 1
        );
      }

      // If storeDate is on a weekend, adjust to the next Monday
      const dayOfWeek = storeDate.getDay(); // Sunday = 0, Saturday = 6
      if (dayOfWeek === 6) { // Saturday: add 2 days
        storeDate.setDate(storeDate.getDate() + 2);
      } else if (dayOfWeek === 0) { // Sunday: add 1 day
        storeDate.setDate(storeDate.getDate() + 1);
      }
      const day = storeDate.toLocaleDateString("ru-RU");

      // Store in storage via API (dead path /storage; реальный flow → /admin/archives)
      await client.post("/storage", {
        day,
        username: currentOrder.username,
        userId: currentOrder.userId,
        author: author,
        bookName: bookName,
        pdfs: pdfUrls,
        pages: Number(pages),
        express: isExpress ? 'express_print' : 'none',
      });

      // 3) Continue your existing logic to update the user in 'users' collection
      await client.patch(`/orders/${currentOrder.username}`, {
        link: `https://analytics-f26ac.web.app/storage/${day}/${currentOrder.username}`,
        pages,
        bookName,
        status: "Готово под печать",
      });

      // Add a new notification
      if (currentUser) {
        await client.post("/notifications", {
          userId: String(currentUser.id),
          message: `Книга ${currentOrder.username} (${currentOrder.payment || "N/A"}/${
            currentOrder.fullPayment || "N/A"
          }) утвердилась и готова к печати`,
          timestamp: new Date().toISOString(),
        });
      }

      // Update local state
      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.username === currentOrder.username
            ? {
                ...o,
                link: pdfUrls[pdfUrls.length - 1] || "",
                status: "Готово под печать",
              }
            : o
        )
      );

      setLoadingSave(false);
      handlePopupClose();
    } catch (error) {
      console.error("Error saving link:", error);
      alert("Ошибка!");
      setLoadingSave(false);
    }
  };


  // «Скинуть клиенту» — попап выбора версии; статус «Отредачено» ставит бэк
  // при отправке (POST /api/proofs/admin).
  const handleApprove = (order: Order) => setSendOrder(order);

  const getStatusClassName = (status: string) => {
    switch (status) {
      case "Пишет":
        return "status-writing";
      case "Заполнил":
        return "status-filled";
      case "Отредачено":
        return "status-edited";
      case "Отпечатано":
        return "status-printed";
      case "Доставлено":
        return "status-delivered";
      case "Готово под печать":
        return "status-approved";
      case "Отложено":
        return "status-ignored";
      case "Возврат":
        return "status-return";
      default:
        return "";
    }
  };

  const formatDate2 = (date: Date | null): string => {
    if (!date) return "-";
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
    };
    const formatter = new Intl.DateTimeFormat("ru-RU", options);
    return formatter.format(date).replace(" г.,", "").replace(":00", "");
  };

  const sortedOrders = useMemo(() => {
    return orders.slice().sort((a, b) => {
      if (a.status === "Отредачено" && b.status !== "Отредачено") return -1;
      if (a.status !== "Отредачено" && b.status === "Отредачено") return 1;
      if (a.status === "Заполнил" && b.status !== "Заполнил") return -1;
      if (a.status !== "Заполнил" && b.status === "Заполнил") return 1;
      return 0;
    });
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (selectedEditor) {
      return sortedOrders.filter((order) => order.editor === selectedEditor);
    }
    return sortedOrders;
  }, [sortedOrders, selectedEditor]);

  const sendWhatsapp = (number: string) => {
    if (!number) return;

    // Remove any non-numeric characters from the number (spaces, dashes, etc.)
    const cleanedNumber = number.replace(/\D/g, "");

    // Ensure that the number starts with a valid country code (e.g., 7 for KZ/RU, 996 for KG, etc.)
    const isValidNumber =
      cleanedNumber.length >= 10 && cleanedNumber.length <= 15; // Adjust for international numbers
    if (!isValidNumber) {
      alert("Некорректный номер телефона");
      return;
    }

    // Construct the message
    let message = "";
    if (currentUser) {
      message = `Здравствуйте! Я редактор Comabooks ${currentUserRecord?.name}! Пишу вам по поводу вашей книги.`;
    } else {
      message = `Здравствуйте! Я редактор Comabooks. Пишу вам по поводу вашей книги.`;
    }

    const encodedMessage = encodeURIComponent(message);

    // Use the cleaned number directly as it already includes the country code
    const whatsappUrl = `https://wa.me/${cleanedNumber}?text=${encodedMessage}`;

    // Open the WhatsApp link in a new tab
    window.open(whatsappUrl, "_blank");
  };

  const renderTable = (orders: Order[], title: string) => (
    <div className="section" style={{ marginBottom: "40px" }}>
      <h3 className="section-title">{title}</h3>
      <table className="tbl">
        <thead>
          <tr>
            <th>Юзернейм</th>
            <th>Ватсап</th>
            <th>Заполнили книгу</th>
            <th>Статус</th>
            <th>Действие</th>
            <th>Редактор</th>
            <th>Комментарий</th>
            <th>snippex</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.userId}>
              <td
                style={{ color: order.isInfluencer ? "#FFC000" : "", cursor: "pointer" }}
                onClick={() => handleEditOpen(order.username)}
              >
                {/* Клиент прислал правки к последней отправке. Пропадает, когда
                    редактор скидывает новую версию (бэк закрывает старую). */}
                {withChanges.has(String(order.userId)) && order.status === "Отредачено" && (
                  <span className="changes-mark" title="Клиент прислал правки к вёрстке">
                    !
                  </span>
                )}
                {order.username}
              </td>
              <td
                onClick={() => sendWhatsapp(order.whatsapp)}
                style={{ textDecoration: "underline", cursor: "pointer" }}
              >
                {order.whatsapp || "---"}
              </td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(order)}
              >
                {formatDate2(order.finishedAt ?? null)}
              </td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(order)}
              >
                <span className={`badge ${getStatusClassName(order.status)}`}>
                  {order.status}
                </span>
              </td>
              <td>
                {(order.status === "Заполнил" || order.status === "Отредачено") && (
                  <button
                    className="btn-action"
                    onClick={() => handleApprove(order)}
                  >
                    {order.status === "Заполнил" ? "Скинуть клиенту" : "Скинуть новую версию"}
                  </button>
                )}
                {order.status === "Отредачено" && (
                  <button
                    className="btn-action"
                    onClick={() => setApprovalOrder(order)}
                  >
                    Отправить на печать
                  </button>
                )}
              </td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(order)}
              >
                {order.editor}
              </td>
              <td
                style={{ cursor: "pointer" }}
                onClick={() => handleRowClick(order)}
              >
                {order.comment || "---"}
              </td>
              <td>{snippexLinked.get(String(order.userId)) || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  const urepOrders = filteredOrders.filter(
    (order) => isEditorPriority(order.express) && order.status !== "Готово под печать"
  );
  const approvedOrders = filteredOrders.filter(
    (order) => order.status === "Отредачено"
  );
  const editedOrders = orders.filter(
    (order) => order.status === "Готово под печать"
  );
  const regularOrders = filteredOrders.filter(
    (order) =>
      !isEditorPriority(order.express) &&
      order.status !== "Готово под печать" &&
      order.status !== "Отредачено"
  );

  const deadlineGroups = useMemo(() => {
    const groups = new Map<number, Order[]>();
    for (const order of regularOrders) {
      const days = order.daysUntilDeadline;
      if (!groups.has(days)) {
        groups.set(days, []);
      }
      groups.get(days)!.push(order);
    }
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
  }, [regularOrders]);

  const handleUserUpdate = (updatedUser: { userId: string } & Record<string, any>) => {
    setOrders((prevOrders) =>
      prevOrders.map((existing) =>
        existing.userId === updatedUser.userId
          ? { ...existing, ...updatedUser }
          : existing
      )
    );
  };

  const preventDragHandler = (e: { preventDefault: () => void }) => {
    e.preventDefault();
  };

  const handleFile1Change = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0] ?? null;
    setFile1(newFile);

    if (newFile) {
      try {
        const file1Pages = await getPageCount(newFile);
        const currentPages = parseInt(pages) || 0;
        setPages(String(Math.max(file1Pages, currentPages)));
        console.log(file1Pages);
      } catch (error) {
        console.error("Error getting page count for file1:", error);
      }
    }
  };

  const handleFile2Change = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = e.target.files?.[0] ?? null;
    setFile2(newFile);

    if (newFile) {
      try {
        const file2Pages = await getPageCount(newFile);
        const currentPages = parseInt(pages) || 0;
        setPages(String(Math.max(file2Pages, currentPages)));
        console.log(file2Pages);
      } catch (error) {
        console.error("Error getting page count for file2:", error);
      }
    }
  };

  const questionTemplateToRecipientMap: { [key: string]: string } = {
    "65ce99cab77b9d31123c8fe8": "Девушка(рус)",
    "65dda647dc778d94d0928969": "Девушка(рус)",
    "65ec32098563e6c8d7d19b47": "Девушка(анг)",
    "65ec32bffc68f8ec35c47182": "Парень(рус)",
    "65fb40c5b63f0df17f6ce6ae": "Мама(рус)",
    "65fb7789f6d6c9118d3caead": "Сестра(рус)",
    "660e01f1fd89fc1e241dd19d": "Себе(рус)",
    "660e02b2ce933929f288c10c": "Парень(рус)",
    "660e255e4d408cc2c1155322": "Гей(рус)",
    "66137cae18ab6072f1cea1df": "Парень(анг)",
    "6614f4093cfbb2d01710d3fe": "Девушка(каз)",
    "66163e630f78aa6b9f522b09": "Девушка(каз)",
    "661970fd80f5c5317e0882c3": "Подруга(рус)",
    "6624bbb268a76a62345780d0": "Друг(рус)",
    "6633b5a9d756354cd72cdce3": "Папа(рус)",
    "663c82d1dbc2b382da139155": "Дочь(рус)",
    "6646846c04d73737d26e56bd": "Мама(каз)",
    "664684ee02d2bee370557777": "Родители(рус)",
    "665afb73525a17e9d582152a": "Папа(каз)",
    "665afbc4b5d1639649ba7383": "Родители(каз)",
    "6666a29a5fb6c1ec70816c19": "Руководитель(рус)",
    "66796202bec27bef793297e8": "Сын(рус)",
  };

  return (
    <Layout>
      {loading && (
        <div className="spinner2">
          <img onDragStart={preventDragHandler} src={loadingcat} />
        </div>
      )}
      {/* {user && user.uid === "quWtgwTRa0f4UHN73VrPtOrPBhD2" && <UpdateExcel />} */}

      <div className="editor-filters">
        <button
          className={
            selectedEditor === null ? "filter-chip filter-chip--active" : "filter-chip"
          }
          onClick={() => setSelectedEditor(null)}
        >
          Все
        </button>
        {Object.values(allUsers)
          .filter((editor: { role: string }) => editor.role === "editor" || editor.role === "headeditor")
          .map((editor: { role: string; name: string; userId: number }) => (
            <button
              key={editor.userId}
              className={
                selectedEditor === editor.name
                  ? "filter-chip filter-chip--active"
                  : "filter-chip"
              }
              onClick={() => setSelectedEditor(editor.name)}
              onContextMenu={(e) => {
                e.preventDefault();
                // Open the assign popup with this editor as the chosen one
                setAssignEditorName(editor.name);
                setAssignEditorPopupOpen(true);
              }}
            >
              {editor.name}, {orders.filter((o) => o.editor === editor.name && o.status !== "Готово под печать").length}
            </button>
          ))}
      </div>

      {renderTable(urepOrders, `УРЭП, ${urepOrders.length}`)}
      {deadlineGroups.map(([days, group]) => (
        <React.Fragment key={days}>
          {renderTable(group, `${getDeadlineLabel(days)}, ${group.length}`)}
        </React.Fragment>
      ))}
      {renderTable(approvedOrders, `Ждет утверждения, ${approvedOrders.length}`)}
      {renderTable(editedOrders, "Готово под печать")}

      {assignEditorPopupOpen && (
        <div className="overlay">
          <div className="popup" style={{ width: "800px" }}>
            <div className="popup__title">
              Назначить редактора: {assignEditorName}, {selectedOrdersToAssign.length}
            </div>
            <div
              className="assign-list"
              style={{
                maxHeight: "300px",
                maxWidth: "790px",
                overflowY: "auto",
              }}
            >
              {(() => {
                // «Готово под печать» без редактора бывает (клиент пропустил
                // редактуру или сам утвердил вёрстку) — назначать там нечего.
                const unassigned = orders.filter(
                  (order) => !order.editor && order.status !== "Готово под печать"
                );
                const urepUnassigned = unassigned.filter((o) => isEditorPriority(o.express));
                const regularUnassigned = unassigned
                  .filter((o) => !isEditorPriority(o.express))
                  .sort((a, b) => a.daysUntilDeadline - b.daysUntilDeadline);
                const groups = new Map<number, Order[]>();
                for (const order of regularUnassigned) {
                  const days = order.daysUntilDeadline;
                  if (!groups.has(days)) groups.set(days, []);
                  groups.get(days)!.push(order);
                }
                const sortedGroups = Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
                return (<>
                  {urepUnassigned.length > 0 && (
                    <React.Fragment>
                      <div style={{ width: "100%", fontWeight: 600, fontSize: "13px", marginTop: "5px" }}>
                        УРЭП ({urepUnassigned.length})
                      </div>
                      {urepUnassigned.map((order) => (
                        <div key={order.userId} className="assign-item">
                          <span>
                            {order.username}:{" "}
                            {questionTemplateToRecipientMap[order.recipient] || "---"}
                          </span>
                          <input
                            type="checkbox"
                            checked={selectedOrdersToAssign.includes(order.username)}
                            onChange={(e) =>
                              handleCheckboxChange(order.username, e.target.checked)
                            }
                          />
                        </div>
                      ))}
                    </React.Fragment>
                  )}
                  {sortedGroups.map(([days, groupOrders]) => (
                  <React.Fragment key={days}>
                    <div style={{ width: "100%", fontWeight: 600, fontSize: "13px", marginTop: "5px" }}>
                      {getDeadlineLabel(days)} ({groupOrders.length})
                    </div>
                    {groupOrders.map((order) => (
                      <div key={order.userId} className="assign-item">
                        <span>
                          {order.username}:{" "}
                          {questionTemplateToRecipientMap[order.recipient] || "---"}
                        </span>
                        <input
                          type="checkbox"
                          checked={selectedOrdersToAssign.includes(order.username)}
                          onChange={(e) =>
                            handleCheckboxChange(order.username, e.target.checked)
                          }
                        />
                      </div>
                    ))}
                  </React.Fragment>
                ))}
                </>);
              })()}
            </div>
            <div className="popup__actions">
              <button className="btn-popup" onClick={handleAssignEditorSave}>
                Сохранить
              </button>
              <button
                className="btn-popup"
                onClick={() => setAssignEditorPopupOpen(false)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Старый «Отправка книги на печать» попап удалён — заменён на ApprovalPopup. */}
      {approvalOrder && (
        <ApprovalPopup
          userId={approvalOrder.userId}
          username={approvalOrder.username}
          mode="full"
          onClose={() => setApprovalOrder(null)}
          onSaved={() => setApprovalOrder(null)}
        />
      )}

      {sentNotice && <div className="sent-notice">{sentNotice}</div>}

      {sendOrder && (
        <SendToClientPopup
          userId={sendOrder.userId}
          username={sendOrder.username}
          onClose={() => setSendOrder(null)}
          onSent={(notify) => {
            const sent = sendOrder;
            setSentNotice(`${sent.username}: ${sentToast(notify)}`);
            window.setTimeout(() => setSentNotice(null), 6000);
            setOrders((prev) =>
              prev.map((o) => (o.username === sent.username ? { ...o, status: "Отредачено" } : o))
            );
            setWithChanges((prev) => {
              const next = new Set(prev);
              next.delete(String(sent.userId));
              return next;
            });
          }}
        />
      )}

      {selectedUser && (
        <User
          onUpdate={handleUserUpdate}
          user={selectedUser}
          onClose={handleCloseUser}
          deadline={getDeadlineLabel(selectedUser.daysUntilDeadline)}
        />
      )}
    </Layout>
  );
};

export default EditorNew;
